"use client";

import { useEffect, useRef } from "react";
import { useSWRConfig } from "swr";
import { API_BASE_URL } from "@/lib/config";
import { motion } from "framer-motion";
import { ScrollText, CircleCheckBig } from "lucide-react";
import { useArtifact } from "@/hooks/use-artifact";
import { getViewModeChrome } from "../view-mode-chrome";

interface GenerateSummarySuccessProps {
  sessionId?: string;
  result?: {
    summary?: string;
    status?: string;
    error_type?: string;
    error_message?: string;
  };
  hideContent?: boolean;
  triggerRefresh?: boolean;
  viewMode?: string;
}

export function GenerateSummarySuccess({
  sessionId,
  result,
  hideContent = false,
  triggerRefresh = false,
  viewMode,
}: GenerateSummarySuccessProps) {
  const chrome = getViewModeChrome(viewMode);
  const { mutate } = useSWRConfig();
  const { setArtifact } = useArtifact();
  const didRunRef = useRef(false);

  // Trigger mutation when component renders to refresh active run data
  useEffect(() => {
    if (!triggerRefresh || !sessionId) return;
    if (didRunRef.current) return;
    didRunRef.current = true;
    mutate(`${API_BASE_URL}/strategies/active-run-details/${sessionId}`);
  }, [triggerRefresh, sessionId, mutate]);

  // Auto-switch to summary view after successful generation
  useEffect(() => {
    if (!triggerRefresh || !sessionId) return;
    // Signal to WorkflowArtifact to switch to summary view
    setArtifact((prev) => ({ ...prev, workflowView: "summary" }));
  }, [triggerRefresh, sessionId, setArtifact]);

  // Check if this is a canceled state
  const isCanceled = result?.status === "canceled";

  // Don't render anything if canceled
  if (isCanceled) {
    return null;
  }

  // Extract summary preview (first 100 chars)
  const summaryPreview = result?.summary
    ? result.summary.substring(0, 100) + (result.summary.length > 100 ? "..." : "")
    : "Summary generated successfully";

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      className="w-full"
    >
      <div className={`rounded-md border ${chrome.card} overflow-hidden`}>
        <div className="px-3 py-2 flex items-center justify-between gap-3 min-w-0">
          <div className="flex items-center gap-2.5 min-w-0 flex-1">
            <ScrollText className={`w-3.5 h-3.5 ${chrome.iconMuted} shrink-0`} strokeWidth={2} />
            <div className="flex flex-col min-w-0 flex-1 overflow-hidden">
              <div className={`text-xs font-medium ${chrome.text} leading-tight line-clamp-1`}>
                Workflow summary generated
              </div>
              <span className={`text-[11px] ${chrome.subtext} truncate`}>
                {summaryPreview}
              </span>
            </div>
          </div>
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.1, type: "spring", stiffness: 300 }}
            className="shrink-0"
          >
            <CircleCheckBig className="h-4 w-4 text-green-500" />
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
}
